import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from '@uirouter/react';
import { lightFormat } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { useActivityData } from './useActivityData';
import TableSkeleton from '../../../../core/components/TableSkeleton';
import StaticTable, { StaticTableColumn } from '../../../../core/components/StaticTable';
import ProgressBar from '../../../../core/components/ProgressBar';
import InfoTooltip from '../../../../core/components/InfoTooltip';
import Button from '../../../../core/components/Button';
import Tooltip from '../../../../core/components/Tooltip';
import {
    ActivityCurrentItem,
    ActivityFinishedJobItem,
    ActivityInboundItem,
    ActivityLockItem,
    ActivityOtherJobItem,
    ActivityReEvalItem,
} from '../../data/datamonitorRepo';

const REFRESH_MS = 20000;
const IDLE_LIMIT_MS = 20 * 60 * 1000;

// legacy Activity template renders every date as 'M/d/yyyy h:mm a' (no seconds)
const fmtDate = (d: Date | null): string => (d ? lightFormat(d, 'M/d/yyyy h:mm a') : '');

// header cell with an info-tip, matching the legacy column tooltips
const tipHeader = (label: string, title: string, message: string): JSX.Element => (
    <InfoTooltip
        message={message}
        title={title}
        direction="top"
    >
        <span>{label}</span>
    </InfoTooltip>
);

const inboundColumns: StaticTableColumn<ActivityInboundItem>[] = [
    { key: 'type', header: 'Data File Type', render: r => r.Type },
    {
        key: 'received',
        header: tipHeader(
            'Total Received',
            'Total Received',
            'Number of files received in last 12 hours. Includes files in all states (received, errors, processed, skipped, duplicates and empty).',
        ),
        render: r => r.TotalReceived,
    },
    {
        key: 'processed',
        header: tipHeader(
            'Total Processed',
            'Total Processed',
            'Number of files processed in last 12 hours. Includes errors, processed, skipped, duplicates and empty files.',
        ),
        render: r => r.TotalProcessed,
    },
    {
        key: 'lastReceived',
        header: tipHeader('Last Received', 'Last Received', 'Time most recent file was received.'),
        render: r => (r.LastReceived ? fmtDate(r.LastReceived) : 'No New Files'),
    },
    {
        key: 'lastProcessed',
        header: tipHeader('Last Processed', 'Last Processed', 'Time most recent file was processed.'),
        render: r => (r.LastProcessed ? fmtDate(r.LastProcessed) : 'No New Files'),
    },
];

const currentColumns: StaticTableColumn<ActivityCurrentItem>[] = [
    { key: 'name', header: 'Name', render: r => r.Name },
    {
        key: 'progress',
        header: 'Progress',
        render: r => (
            <ProgressBar
                value={r.ProcessingCount}
                max={r.EstimatedCount}
            />
        ),
    },
    {
        key: 'start',
        header: tipHeader(
            'Process Start Time',
            'Process Start Time',
            'Process Start Time includes start up, actual processing, pauses.',
        ),
        render: r => fmtDate(r.ProcessStartTime),
    },
    {
        key: 'processingTime',
        header: tipHeader(
            'Processing Time',
            'Processing Time',
            'Processing Time is the amount of time between the Process Start Time and the last data refresh.',
        ),
        render: r => fmtDate(r.ProcessingTime),
    },
    {
        key: 'ping',
        header: tipHeader(
            'Last Ping from Activity',
            'Last Ping from Activity',
            'Last Ping from Activity is the last time the job logged an action.',
        ),
        render: r => fmtDate(r.LogTime),
    },
];

const finishedColumns: StaticTableColumn<ActivityFinishedJobItem>[] = [
    { key: 'name', header: 'Name', render: r => r.Name },
    { key: 'runs', header: 'Total Runs', render: r => r.TotalRuns },
    { key: 'processed', header: 'Total Processed', render: r => r.TotalProcessed },
    { key: 'lastRun', header: 'Last Run', render: r => fmtDate(r.LastRun) },
    { key: 'avg', header: 'Avg Duration', render: r => r.AvgDurationText },
    { key: 'total', header: 'Total Duration', render: r => r.TotalDurationText },
];

const reEvalColumns: StaticTableColumn<ActivityReEvalItem>[] = [
    {
        key: 'status',
        header: tipHeader(
            'Status',
            'Status',
            'NEW means the job is in queue. RUNNING indicates the job started but has not completed.',
        ),
        render: r => r.Status,
    },
    { key: 'createBy', header: 'Create By', render: r => r.CreateBy },
    {
        key: 'createDate',
        header: tipHeader('Create Date', 'Create Date', 'Create Date is the time when the job was created.'),
        render: r => fmtDate(r.CreateDate),
    },
    {
        key: 'startDate',
        header: tipHeader('Start Date', 'Start Date', 'Start Date is when the Re-Eval job began processing.'),
        render: r => fmtDate(r.StartDate),
    },
    {
        key: 'processingTime',
        header: tipHeader(
            'Processing Time',
            'Processing Time',
            'Processing Time is the amount of time between the Start Time and the last data refresh.',
        ),
        render: r => r.ProcessingTime,
    },
    { key: 'details', header: 'Details', render: r => r.Details },
];

const otherColumns: StaticTableColumn<ActivityOtherJobItem>[] = [
    { key: 'name', header: 'Name', render: r => r.Name },
    {
        key: 'status',
        header: tipHeader(
            'Status',
            'Status',
            'NEW means the job is in queue. RUNNING indicates the job started but has not completed.',
        ),
        render: r => r.Status,
    },
    { key: 'createBy', header: 'Create By', render: r => r.UserId },
    {
        key: 'startDate',
        header: tipHeader('Start Date', 'Start Date', 'Start Time is when the user job began processing.'),
        render: r => fmtDate(r.StartDate),
    },
    {
        key: 'processingTime',
        header: tipHeader(
            'Processing Time',
            'Processing Time',
            'Processing Time is the amount of time between the StartTime and the last data refresh.',
        ),
        render: r => r.ProcessingTime,
    },
];

const lockColumns: StaticTableColumn<ActivityLockItem>[] = [
    { key: 'key', header: 'Key', render: r => r.Key },
    { key: 'heldBy', header: 'HeldBy', render: r => r.HeldBy },
    { key: 'acquired', header: 'Acquired', render: r => fmtDate(r.Acquired) },
];

type ActivitySectionProps<T> = {
    title: string;
    subtitle?: string;
    columns: StaticTableColumn<T>[];
    rows: T[];
    loading?: boolean;
};

// legacy hides the table when its list is empty but always keeps the section header
const ActivitySection = <T,>({ title, subtitle, columns, rows, loading }: ActivitySectionProps<T>): JSX.Element => (
    <div className="datamonitor-activity-section">
        <h5 className="datamonitor-activity-header">{title}</h5>
        {subtitle ? <p className="datamonitor-activity-subtitle">{subtitle}</p> : null}
        {rows.length > 0 ? (
            <StaticTable
                columns={columns}
                rows={rows}
                loading={loading}
            />
        ) : null}
    </div>
);

const ActivityTab = (): JSX.Element => {
    const router = useRouter();
    const { init, waiting, error, data, load } = useActivityData();
    const [monitoring, setMonitoring] = useState(false);
    const lastActivityRef = useRef(Date.now());

    // legacy loads once on tab entry; polling only runs while "Refresh Data" is on
    useEffect(() => {
        lastActivityRef.current = Date.now();
        load();
    }, [load]);

    useEffect(() => {
        if (!monitoring) {
            return undefined;
        }
        const timer = window.setInterval(() => {
            // legacy: bounce idle users back home instead of polling forever
            if (Date.now() - lastActivityRef.current > IDLE_LIMIT_MS) {
                window.clearInterval(timer);
                router.stateService.go('home', {}, { reload: 'home' });
                return;
            }
            load();
        }, REFRESH_MS);
        return (): void => window.clearInterval(timer);
    }, [monitoring, load, router]);

    const toggleMonitoring = useCallback((): void => {
        lastActivityRef.current = Date.now();
        setMonitoring(m => !m);
    }, []);

    if (init) {
        return (
            <TableSkeleton
                columns={6}
                rows={5}
                sections={3}
            />
        );
    }

    return (
        <div className="datamonitor-activity">
            <div className="datamonitor-activity-top">
                <Tooltip
                    content="Refreshes page data every 20 seconds."
                    direction="left"
                >
                    <Button
                        cssName="btn-secondary datamonitor-activity-refresh"
                        onClick={toggleMonitoring}
                        disabled={waiting}
                    >
                        <FontAwesomeIcon
                            icon={faArrowsRotate}
                            className={monitoring ? 'is-spinning' : ''}
                        />
                        <span>Refresh Data</span>
                    </Button>
                </Tooltip>
            </div>
            {error ? <div className="datamonitor-activity-error">{error}</div> : null}
            <ActivitySection
                title="Inbound Data File Processing"
                columns={inboundColumns}
                rows={data.inbound}
                loading={waiting}
            />
            <ActivitySection
                title="Current Processing Activities"
                columns={currentColumns}
                rows={data.current}
                loading={waiting}
            />
            <ActivitySection
                title="Recent Finished Jobs ~ 6 hours"
                columns={finishedColumns}
                rows={data.finished}
                loading={waiting}
            />
            <ActivitySection
                title="Re-Eval Jobs"
                columns={reEvalColumns}
                rows={data.reEval}
                loading={waiting}
            />
            <ActivitySection
                title="Other Jobs"
                subtitle="'Other Jobs' include NDC Imports and Inventory Rebuilds."
                columns={otherColumns}
                rows={data.other}
                loading={waiting}
            />
            <ActivitySection
                title="Existing Database Locks"
                subtitle="This section is used by the Engineering teams to understand what locks currently exist in the database."
                columns={lockColumns}
                rows={data.locks}
                loading={waiting}
            />
        </div>
    );
};

export default ActivityTab;
